import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import type { AdminMeResponse } from '@immo/api-contract';

/*
 * Topbar slot plumbing.
 *
 * The AdminLayout topbar exposes two slot elements (leading + trailing).
 * Pages portal their own content into them via <PageTopbar /> so the
 * sidebar+topbar+main grid stays in one component while every page
 * controls what shows up in the topbar.
 */

export type AdminOutletContext = {
  readonly me: AdminMeResponse;
  readonly leadingEl: HTMLElement | null;
  readonly trailingEl: HTMLElement | null;
};

export function useAdminOutletContext(): AdminOutletContext | null {
  return useOutletContext<AdminOutletContext | undefined>() ?? null;
}

type PageTopbarProps = {
  readonly leading?: React.ReactNode;
  readonly trailing?: React.ReactNode;
};

export function PageTopbar({ leading, trailing }: PageTopbarProps) {
  const ctx = useAdminOutletContext();

  if (ctx === null) {
    return null;
  }

  return (
    <>
      {leading !== undefined && ctx.leadingEl !== null && createPortal(leading, ctx.leadingEl)}
      {trailing !== undefined && ctx.trailingEl !== null && createPortal(trailing, ctx.trailingEl)}
    </>
  );
}
